import { useEffect, useRef } from 'react';

import { QueryKey, useInfiniteQuery } from '@tanstack/react-query';

import { getNextPageParam } from '@/lib/constants/pagination';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';

import {
  DEFAULT_SECTION_STALE_TIME,
  IdentifiableRow,
  SectionLoaderPayload,
} from './nested-pagination.types';

interface SectionLoaderProps<TRow extends IdentifiableRow> {
  /** TanStack row id of the parent whose children this section loads — the section key. */
  sectionId: string;
  /** Query key MUST NOT include the cursor (the cursor lives in `pageParam`). */
  queryKey: QueryKey;
  queryFn: (cursor: number | null | undefined) => Promise<PaginatedResponse<TRow>>;
  /**
   * Dedupe id for accumulated pages; the first-seen object wins so row references stay
   * stable across page appends. NOTE: this is the row's API-level dedupe key, which may
   * differ from the registry's `row.id` used for version-gating — keep them distinct.
   */
  getRowId: (row: TRow) => string | number;
  onChange: (sectionId: string, payload: SectionLoaderPayload<TRow>) => void;
  /** Called on unmount so the registry can drop this section's stale state. */
  onUnmount?: (sectionId: string) => void;
  staleTime?: number;
}

/**
 * Headless loader (renders null) for ONE expanded section's child list. Runs its own
 * `useInfiniteQuery` so the cursor lives in `pageParam` (never the query key) and
 * `fetchNextPage`/`hasNextPage` are TanStack-managed. One instance is mounted per
 * expanded parent, so every section paginates fully independently. Accumulated rows +
 * pagination flags are reported upward via `onChange`.
 *
 * TanStack Query v5's `useQueries` cannot host infinite queries — rendering one loader
 * component per section is the supported mechanism for a dynamic number of infinite queries.
 */
export function SectionLoader<TRow extends IdentifiableRow>({
  sectionId,
  queryKey,
  queryFn,
  getRowId,
  onChange,
  onUnmount,
  staleTime = DEFAULT_SECTION_STALE_TIME,
}: SectionLoaderProps<TRow>) {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => queryFn(pageParam),
    getNextPageParam,
    initialPageParam: undefined as number | null | undefined,
    staleTime,
    // Flatten + dedupe, keeping the first-seen object so references stay stable across
    // page appends (avoids needless downstream enrichment churn).
    select: data => {
      const seen = new Map<string | number, TRow>();
      for (const page of data.pages) {
        for (const row of page.items) {
          const id = getRowId(row);
          if (!seen.has(id)) seen.set(id, row);
        }
      }
      return Array.from(seen.values());
    },
  });

  const { data, hasNextPage, isFetchingNextPage, isLoading, fetchNextPage } = query;

  useEffect(() => {
    onChange(sectionId, {
      rows: data ?? [],
      hasNextPage: !!hasNextPage,
      isFetchingNextPage,
      isLoadingFirstPage: isLoading,
      fetchNextPage: () => {
        void fetchNextPage();
      },
    });
  }, [sectionId, data, hasNextPage, isFetchingNextPage, isLoading, fetchNextPage, onChange]);

  // Notify the registry on true unmount. Refs keep the cleanup from firing on prop-identity
  // changes (which would drop a still-mounted section).
  const sectionIdRef = useRef(sectionId);
  sectionIdRef.current = sectionId;
  const onUnmountRef = useRef(onUnmount);
  onUnmountRef.current = onUnmount;
  useEffect(
    () => () => {
      onUnmountRef.current?.(sectionIdRef.current);
    },
    []
  );

  return null;
}
